import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FaArchive, FaChartBar, FaDownload, FaEdit, FaFilePdf, FaFolderOpen, FaFolderPlus, FaPlus, FaSlidersH } from "react-icons/fa";
import { LIBRARY_CONSTANTS } from "../_MODULES/_API";
import { LibraryGroupModal } from "../_UI/LibraryGroupModal";
import { LibraryPdfModal } from "../_UI/LibraryPdfModal";
import { LibraryPdfReaderModal, downloadLibraryFolder } from "../_UI/LibraryPdfReaderModal";
import { PageHeader } from "../../../../../_CORE/APP/Templates/_UI/PageHeader/_index";
import { AppButton } from "../../../../../_CORE/APP/Templates/_UI/Button/_index";
import { FieldDate, FieldInput, FieldSelect } from "../../../../../_CORE/APP/Templates/_UI/Form/_index";
import { EmptyState, Skeleton } from "../../../../../_CORE/APP/Templates/_UI/Kit/_index";
import { useApiQuery } from "../../../../../_CORE/APP/Hooks/Hooks/_Records/_useApiQuery";
import { useApiActionMutation } from "../../../../../_CORE/APP/Hooks/Hooks/_Actions/_useApiActionMutation";
import { confirm } from "../../../../../_CORE/APP/Templates/_UI/Confirm/AppConfirmHost";
import { onApiAction } from "../../../../_UI/onApiAction";
import { REACT_HOT_TOAST_ERROR, REACT_HOT_TOAST_SUCCESS } from "../../../../../_CORE/HELPERS/reactHotToast.helper";
import { ViewLayout } from "../../../../_UI/ViewLayout";
import { ViewToolbar } from "../../../../_UI/ViewToolbar";
import { StatisticsModal } from "../../../../_UI/StatisticsModal";
import { AdvancedFilterPanel, onDateMatches } from "../../../../_UI/AdvancedFilterPanel";
import { SearchFilter, onMatchesSearch } from "../../../../_UI/SearchFilter";
import { CardActionMenu } from "../../../../_UI/CardActionMenu";
import { isDuplicateRecord } from "../../../../_UI/recordUniqueness";
import "../_UI/_Styles/Library.css";

const formatSize = (bytes) => {
    const value = Number(bytes || 0);
    if (value < 1024) return `${value} B`;
    if (value < 1048576) return `${Math.round(value / 1024)} KB`;
    return `${(value / 1048576).toFixed(1)} MB`;
};

const titleFromFile = (name = "") => String(name)
    .replace(/\.pdf$/i, "")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim() || "Documento PDF";

const UNGROUPED_FOLDER = {
    id: "UNGROUPED",
    name: "Sin grupo",
    description: "",
    color: "#7b8498",
    isVirtual: true,
};

export const LibraryViewMain = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [search, setSearch] = useState("");
    const [groupModalOpen, setGroupModalOpen] = useState(false);
    const [editingGroup, setEditingGroup] = useState(null);
    const [pdfModalOpen, setPdfModalOpen] = useState(false);
    const [editingPdf, setEditingPdf] = useState(null);
    const [uploadGroupId, setUploadGroupId] = useState("");
    const [readerGroup, setReaderGroup] = useState(null);
    const [statisticsOpen, setStatisticsOpen] = useState(false);
    const [advancedOpen, setAdvancedOpen] = useState(false);
    const [advanced, setAdvanced] = useState({ favorite: "ALL", author: "", state: "ALL", dateField: "UPDATED", dateRange: [null, null], dateExact: null });
    const dialogClosingRef = useRef(false);

    const {
        apiGet: getGroups, useApiData: groupsData, useApiLoading: groupsLoading, mutateLocal: mutateGroups,
    } = useApiQuery({ minTimeQuery: 180, enablePagination: false, startLoading: true });
    const {
        apiGet: getPdfs, useApiData: pdfsData, useApiLoading: pdfsLoading, mutateLocal: mutatePdfs,
    } = useApiQuery({ minTimeQuery: 180, enablePagination: false, startLoading: true });
    const { actionMutate, useActionApiIsLoading } = useApiActionMutation({
        isToastError: true, isToastSuccess: true, minTimeMutate: 180,
    });

    useEffect(() => {
        getGroups({ endpoint: LIBRARY_CONSTANTS.GROUP_LIST });
        getPdfs({ endpoint: LIBRARY_CONSTANTS.PDF_LIST });
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const groups = Array.isArray(groupsData) ? groupsData : [];
    const pdfs = Array.isArray(pdfsData) ? pdfsData : [];
    const totalSize = useMemo(() => pdfs.reduce((sum, pdf) => sum + Number(pdf.size || 0), 0), [pdfs]);

    const folders = useMemo(() => {
        const ungrouped = pdfs.filter((pdf) => !pdf.groupId);
        const result = [];
        // Todo lo pendiente de organizar debe quedar primero y visible.
        if (ungrouped.length) result.push({ ...UNGROUPED_FOLDER, documents: ungrouped });
        result.push(...groups.map((group) => ({
            ...group,
            documents: pdfs.filter((pdf) => String(pdf.groupId || "") === String(group.id)),
        })));
        return result;
    }, [groups, pdfs]);

    const advancedCount = [
        advanced.favorite !== "ALL", String(advanced.author || "").trim(), advanced.state !== "ALL",
        advanced.dateRange?.[0] || advanced.dateRange?.[1], advanced.dateExact,
    ].filter(Boolean).length;
    const clearAdvanced = () => setAdvanced({ favorite: "ALL", author: "", state: "ALL", dateField: "UPDATED", dateRange: [null, null], dateExact: null });

    const visibleFolders = useMemo(() => folders.filter((folder) => {
        const matchingDocs = folder.documents.filter((pdf) => {
            if (advanced.favorite === "YES" && !pdf.isFavorite) return false;
            if (advanced.favorite === "NO" && pdf.isFavorite) return false;
            if (String(advanced.author || "").trim() && !onMatchesSearch(advanced.author, [pdf.author])) return false;
            const dateValue = advanced.dateField === "CREATED" ? pdf.createdAt : pdf.updatedAt;
            if (!onDateMatches(dateValue, advanced.dateRange, advanced.dateExact)) return false;
            return true;
        });
        if (advanced.state === "WITH" && folder.documents.length === 0) return false;
        if (advanced.state === "EMPTY" && folder.documents.length > 0) return false;
        if (advancedCount > 0 && advanced.state !== "EMPTY" && matchingDocs.length === 0) return false;
        return onMatchesSearch(search, [
            folder.name,
            folder.description,
            ...matchingDocs.flatMap((pdf) => [pdf.title, pdf.author, pdf.description, pdf.originalName]),
        ]);
    }), [folders, search, advanced, advancedCount]);

    const activeReaderGroup = useMemo(() => {
        if (!readerGroup) return null;
        return folders.find((folder) => String(folder.id) === String(readerGroup.id)) || readerGroup;
    }, [folders, readerGroup]);

    useEffect(() => {
        const routed = searchParams.get("group");
        if (!routed) {
            setReaderGroup(null);
            return;
        }
        const found = folders.find((folder) => String(folder.id) === String(routed));
        if (!found) return;
        setReaderGroup((current) => String(current?.id || "") === String(routed) ? current : found);
    }, [searchParams, folders]);

    useEffect(() => {
        const dialog = searchParams.get("dialog");
        if (dialogClosingRef.current) {
            if (!dialog) dialogClosingRef.current = false;
            return;
        }
        if (!dialog) return;
        if (dialog === "group-new") { setEditingGroup(null); setGroupModalOpen(true); return; }
        if (dialog.startsWith("group:")) {
            const id = dialog.slice("group:".length);
            const group = groups.find((row) => String(row.id) === String(id));
            if (group) { setEditingGroup(group); setGroupModalOpen(true); }
            return;
        }
        if (dialog === "pdf-new" || dialog.startsWith("pdf-new:")) {
            const groupId = dialog.includes(":") ? dialog.slice("pdf-new:".length) : "";
            setEditingPdf(null);
            setUploadGroupId(groupId === "UNGROUPED" ? "" : groupId);
            setPdfModalOpen(true);
            return;
        }
        if (dialog.startsWith("pdf:")) {
            const id = dialog.slice("pdf:".length);
            const pdf = pdfs.find((row) => String(row.id) === String(id));
            if (pdf) { setEditingPdf(pdf); setUploadGroupId(""); setPdfModalOpen(true); }
        }
    }, [searchParams, groups, pdfs]);

    const updateRouteState = (patch = {}) => {
        const next = new URLSearchParams(window.location.search);
        Object.entries(patch).forEach(([key, value]) => {
            if (value === null || value === undefined || value === "") next.delete(key);
            else next.set(key, String(value));
        });
        setSearchParams(next, { replace: true });
    };

    const openReader = (folder, fileId = null) => {
        setReaderGroup(folder);
        updateRouteState({ group: folder?.id, file: fileId || null, preview: null, dialog: null });
    };

    const closeReader = () => {
        updateRouteState({ group: null, file: null, preview: null, fileAdvanced: null, dialog: null });
        setReaderGroup(null);
    };

    const openNewGroup = () => {
        setEditingGroup(null);
        setGroupModalOpen(true);
        updateRouteState({ dialog: "group-new" });
    };

    const openEditGroup = (group) => {
        if (!group || group.isVirtual) return;
        setEditingGroup(group);
        setGroupModalOpen(true);
        updateRouteState({ dialog: `group:${group.id}` });
    };

    const openUpload = (groupId = "") => {
        setEditingPdf(null);
        setUploadGroupId(groupId === "UNGROUPED" ? "" : (groupId || ""));
        setPdfModalOpen(true);
        updateRouteState({ dialog: groupId ? `pdf-new:${groupId}` : "pdf-new" });
    };

    const openEditPdf = (pdf) => {
        setEditingPdf(pdf);
        setUploadGroupId("");
        setPdfModalOpen(true);
        updateRouteState({ dialog: `pdf:${pdf.id}` });
    };

    const saveGroup = async (values) => {
        const edit = !!editingGroup?.id;
        if (isDuplicateRecord({ rows: groups, value: values.name, currentId: editingGroup?.id })) {
            REACT_HOT_TOAST_ERROR("Ya existe una carpeta con ese nombre");
            return false;
        }
        const previous = edit ? { ...editingGroup } : null;
        if (edit) mutateGroups({ id: editingGroup.id, patch: values });
        const response = await actionMutate({
            method: edit ? "PATCH" : "POST",
            endpoint: edit ? LIBRARY_CONSTANTS.GROUP_UPDATE : LIBRARY_CONSTANTS.GROUP_CREATE,
            id: edit ? editingGroup.id : null,
            data: values,
            successMessage: edit ? "Grupo actualizado" : "Grupo creado",
        });
        if (!response.success) {
            if (edit && previous) mutateGroups({ id: previous.id, patch: previous });
            return false;
        }
        if (response.data?.id) {
            if (edit) mutateGroups({ id: editingGroup.id, patch: response.data });
            else mutateGroups({ insert: response.data, position: "end" });
        }
        return true;
    };

    const archiveGroup = async (group) => {
        if (!group || group.isVirtual) return;
        const affected = pdfs.filter((pdf) => String(pdf.groupId) === String(group.id)).map((pdf) => ({ ...pdf }));
        const ok = await confirm.danger(
            "Archivar grupo",
            `Se archiva "${group.name}" junto con ${affected.length} PDF${affected.length === 1 ? "" : "s"}. Podés restaurarlo desde Archivados.`,
        );
        if (!ok) return;
        mutateGroups({ id: group.id, remove: true });
        affected.forEach((pdf) => mutatePdfs({ id: pdf.id, remove: true }));
        if (String(readerGroup?.id || "") === String(group.id)) closeReader();
        const response = await onApiAction({
            method: "PATCH", endpoint: LIBRARY_CONSTANTS.GROUP_ARCHIVE,
            pathParams: { id: group.id }, successMessage: "Grupo archivado",
        });
        if (!response.success) {
            mutateGroups({ insert: group, position: "end" });
            affected.forEach((pdf) => mutatePdfs({ insert: pdf, position: "end" }));
        }
    };

    const savePdf = async (values, selectedFiles) => {
        const edit = !!editingPdf?.id;
        const targetGroup = values.groupId ? groups.find((group) => group.id === values.groupId) || null : null;

        if (edit) {
            if (isDuplicateRecord({
                rows: pdfs, value: values.title || editingPdf.title, currentId: editingPdf.id,
                getValue: (row) => row?.title, scope: values.groupId || "", getScope: (row) => row?.groupId || "",
            })) {
                REACT_HOT_TOAST_ERROR("Ya existe un PDF con ese título en esta carpeta");
                return false;
            }
            const previous = { ...editingPdf };
            const localPatch = { ...values, group: targetGroup };
            mutatePdfs({ id: editingPdf.id, patch: localPatch });
            const response = await actionMutate({
                method: "PATCH", endpoint: LIBRARY_CONSTANTS.PDF_UPDATE, id: editingPdf.id,
                data: values, successMessage: "PDF actualizado",
            });
            if (!response.success) {
                mutatePdfs({ id: previous.id, patch: previous });
                return false;
            }
            if (response.data?.id) mutatePdfs({ id: editingPdf.id, patch: response.data });
            return true;
        }

        const files = Array.isArray(selectedFiles) ? selectedFiles.filter(Boolean) : selectedFiles ? [selectedFiles] : [];
        if (files.length === 0) return false;

        const failedFiles = [];
        let uploadedCount = 0;
        for (const file of files) {
            const uploadTitle = files.length === 1 && values.title ? values.title : titleFromFile(file.name);
            if (isDuplicateRecord({
                rows: pdfs, value: uploadTitle, getValue: (row) => row?.title,
                scope: values.groupId || "", getScope: (row) => row?.groupId || "",
            })) {
                failedFiles.push(file);
                REACT_HOT_TOAST_ERROR(`Ya existe “${uploadTitle}” en esta carpeta`);
                continue;
            }
            const form = new FormData();
            form.append("file", file);
            form.append("title", uploadTitle);
            if (values.description) form.append("description", values.description);
            if (values.author) form.append("author", values.author);
            if (values.groupId) form.append("groupId", values.groupId);
            form.append("isFavorite", values.isFavorite ? "true" : "false");

            const response = await actionMutate({
                method: "POST", endpoint: LIBRARY_CONSTANTS.PDF_UPLOAD, data: form, successMessage: "",
            });
            if (!response.success) {
                failedFiles.push(file);
                continue;
            }
            uploadedCount += 1;
            if (response.data?.id) mutatePdfs({ insert: response.data, position: "end" });
        }

        if (uploadedCount > 0) {
            REACT_HOT_TOAST_SUCCESS(uploadedCount === 1
                ? "PDF agregado a la biblioteca"
                : `${uploadedCount} PDFs agregados a la biblioteca`);
        }
        return { success: failedFiles.length === 0, failedFiles };
    };

    const toggleFavorite = async (pdf) => {
        const previous = !!pdf.isFavorite;
        const next = !previous;
        mutatePdfs({ id: pdf.id, patch: { isFavorite: next } });
        const response = await actionMutate({
            method: "PATCH", endpoint: LIBRARY_CONSTANTS.PDF_UPDATE, id: pdf.id,
            data: { isFavorite: next }, successMessage: next ? "PDF marcado como favorito" : "PDF quitado de favoritos",
        });
        if (!response.success) mutatePdfs({ id: pdf.id, patch: { isFavorite: previous } });
    };

    const archivePdf = async (pdf) => {
        const ok = await confirm.danger(
            "Archivar PDF",
            `Se archiva "${pdf.title}". El archivo se conserva y podés restaurarlo desde Archivados.`,
        );
        if (!ok) return;
        mutatePdfs({ id: pdf.id, remove: true });
        const response = await onApiAction({
            method: "PATCH", endpoint: LIBRARY_CONSTANTS.PDF_ARCHIVE,
            pathParams: { id: pdf.id }, successMessage: "PDF archivado",
        });
        if (!response.success) mutatePdfs({ insert: pdf, position: "end" });
    };

    const exportRows = visibleFolders.map((folder) => ({
        folder: folder.name || "",
        description: folder.description || "",
        pdfs: folder.documents.length,
        authors: [...new Set(folder.documents.map((pdf) => String(pdf.author || "").trim()).filter(Boolean))].join(", "),
        size: formatSize(folder.documents.reduce((sum, pdf) => sum + Number(pdf.size || 0), 0)),
    }));

    const loading = groupsLoading || pdfsLoading;

    return (
        <ViewLayout
            header={(
                <PageHeader
                    title="Biblioteca"
                    icon={<FaFilePdf />}
                    description="Biblioteca personal de PDFs organizada por carpetas."
                    actions={(
                        <div className="LStudio__library_header_actions">
                            <AppButton variant="outline" tone="neutral" icon={<FaChartBar />} isIconOnly title="Estadísticas" aria-label="Estadísticas" onClick={() => setStatisticsOpen(true)} />
                            <AppButton variant="outline" tone="neutral" icon={<FaFolderPlus />} onClick={openNewGroup}>Nueva carpeta</AppButton>
                            <AppButton icon={<FaPlus />} onClick={() => openUpload()}>Subir PDF</AppButton>
                        </div>
                    )}
                />
            )}
            toolbar={<ViewToolbar
                filters={<>
                    <SearchFilter
                        value={search}
                        onChange={setSearch}
                        total={folders.length}
                        matches={visibleFolders.length}
                        placeholder="Buscar carpeta, PDF, autor o archivo..."
                    />
                    <AppButton variant={advancedOpen ? "soft" : "outline"} tone="neutral" icon={<FaSlidersH />}
                        onClick={() => setAdvancedOpen((current) => !current)}>
                        {advancedCount ? `Avanzado (${advancedCount})` : "Avanzado"}
                    </AppButton>
                </>}
                expandedContent={<AdvancedFilterPanel isOpen={advancedOpen} activeCount={advancedCount} onClear={clearAdvanced}>
                    <FieldSelect id="library_adv_state" label="Carpeta" value={advanced.state}
                        onChange={(event) => setAdvanced((current) => ({ ...current, state: event.target.value }))}
                        options={[{ value: "ALL", label: "Todas" }, { value: "WITH", label: "Con PDFs" }, { value: "EMPTY", label: "Vacías" }]} />
                    <FieldSelect id="library_adv_favorite" label="Favoritos" value={advanced.favorite}
                        onChange={(event) => setAdvanced((current) => ({ ...current, favorite: event.target.value }))}
                        options={[{ value: "ALL", label: "Todos" }, { value: "YES", label: "Sólo favoritos" }, { value: "NO", label: "No favoritos" }]} />
                    <FieldInput id="library_adv_author" label="Autor" value={advanced.author}
                        onChange={(event) => setAdvanced((current) => ({ ...current, author: event.target.value }))} placeholder="Ej.: Martin Fowler" />
                    <FieldSelect id="library_adv_date_field" label="Fecha" value={advanced.dateField}
                        onChange={(event) => setAdvanced((current) => ({ ...current, dateField: event.target.value }))}
                        options={[{ value: "UPDATED", label: "Última actualización" }, { value: "CREATED", label: "Fecha de creación" }]} />
                    <FieldDate id="library_adv_from" label="Desde" value={advanced.dateRange?.[0] || null}
                        onChange={(date) => setAdvanced((current) => ({ ...current, dateRange: [date || null, current.dateRange?.[1] || null], dateExact: null }))} isClearable />
                    <FieldDate id="library_adv_to" label="Hasta" value={advanced.dateRange?.[1] || null}
                        onChange={(date) => setAdvanced((current) => ({ ...current, dateRange: [current.dateRange?.[0] || null, date || null], dateExact: null }))} isClearable />
                    <FieldDate id="library_adv_exact" label="Fecha exacta" value={advanced.dateExact}
                        onChange={(date) => setAdvanced((current) => ({ ...current, dateExact: date || null, dateRange: date ? [null, null] : current.dateRange }))} isClearable />
                </AdvancedFilterPanel>}
                total={visibleFolders.length}
                totalSingular="carpeta"
                totalPlural="carpetas"
                exportColumns={[
                    { key: "folder", label: "Carpeta" },
                    { key: "description", label: "Descripción" },
                    { key: "pdfs", label: "PDFs" },
                    { key: "authors", label: "Autores" },
                    { key: "size", label: "Tamaño" },
                ]}
                exportRows={exportRows}
                exportName="biblioteca-carpetas"
            />}
        >
            <div className="LStudio__library">
                <section className="LStudio__library_folder_section">
                    <div className="LStudio__library_folder_section_heading">
                        <strong>Carpetas</strong>
                        <small>{visibleFolders.length} {visibleFolders.length === 1 ? "carpeta" : "carpetas"}</small>
                    </div>

                    {loading && <div className="LStudio__library_folder_loading"><Skeleton variant="box" height={210} /></div>}

                    {!loading && visibleFolders.length === 0 && (
                        <EmptyState
                            icon={<FaFolderOpen />}
                            title={folders.length ? "No encontramos carpetas" : "Todavía no hay carpetas ni PDFs"}
                            description={folders.length
                                ? "Probá con otro término. También podés buscar por el nombre de un PDF y se mostrará la carpeta que lo contiene."
                                : "Creá una carpeta o subí un PDF. Los documentos sin carpeta aparecerán agrupados en ‘Sin grupo’."}
                            action={<AppButton icon={<FaPlus />} onClick={folders.length ? () => { setSearch(""); clearAdvanced(); } : openNewGroup}>{folders.length ? "Limpiar filtros" : "Crear primera carpeta"}</AppButton>}
                        />
                    )}

                    {!loading && visibleFolders.length > 0 && (
                        <div className="LStudio__library_folder_grid">
                            {visibleFolders.map((folder) => (
                                <article
                                    key={folder.id}
                                    className="LStudio__library_folder_card"
                                    data-virtual={folder.isVirtual ? "true" : "false"}
                                    style={{ "--library-group-color": folder.color || "#7b8498" }}
                                    role="button"
                                    tabIndex={0}
                                    onClick={() => openReader(folder)}
                                    onKeyDown={(event) => {
                                        if (event.key === "Enter" || event.key === " ") {
                                            event.preventDefault();
                                            openReader(folder);
                                        }
                                    }}
                                >
                                    <span className="LStudio__library_folder_icon"><FaFolderOpen /></span>
                                    <div className="LStudio__library_folder_copy">
                                        <div className="LStudio__library_folder_titleline">
                                            <strong title={folder.name}>{folder.name}</strong>
                                            <span>{folder.documents.length} PDF{folder.documents.length === 1 ? "" : "s"}</span>
                                        </div>
                                        {folder.description && <p>{folder.description}</p>}
                                    </div>
                                    <div className="LStudio__library_folder_actions" onClick={(event) => event.stopPropagation()}>
                                        <CardActionMenu
                                            title="Opciones de carpeta"
                                            ariaLabel={`Opciones de ${folder.name}`}
                                            items={[
                                                {
                                                    key: "download",
                                                    label: "Descargar carpeta",
                                                    icon: <FaDownload />,
                                                    isDisabled: folder.documents.length === 0,
                                                    onClick: () => downloadLibraryFolder(folder, folder.documents),
                                                },
                                                ...(!folder.isVirtual ? [
                                                    { key: "edit", label: "Editar carpeta", icon: <FaEdit />, onClick: () => openEditGroup(folder) },
                                                    { divider: true },
                                                    { key: "archive", label: "Archivar carpeta", icon: <FaArchive />, tone: "danger", onClick: () => archiveGroup(folder) },
                                                ] : []),
                                            ]}
                                        />
                                    </div>
                                </article>
                            ))}
                        </div>
                    )}
                </section>
            </div>


            <StatisticsModal
                isOpen={statisticsOpen}
                onClose={() => setStatisticsOpen(false)}
                title="Estadísticas · Biblioteca"
                description="Resumen general de la biblioteca."
                metrics={[
                    { key: "pdfs", label: "PDFs", value: pdfs.length, icon: <FaFilePdf />, tone: "accent", isLoading: loading },
                    { key: "folders", label: "Carpetas", value: groups.length, icon: <FaFolderOpen />, tone: "neutral", isLoading: loading },
                    { key: "size", label: "Espacio", value: formatSize(totalSize), icon: <FaDownload />, tone: "neutral", isLoading: loading },
                    { key: "ungrouped", label: "Sin grupo", value: pdfs.filter((pdf) => !pdf.groupId).length, icon: <FaFolderOpen />, tone: "neutral", isLoading: loading },
                    { key: "favorites", label: "Favoritos", value: pdfs.filter((pdf) => pdf.isFavorite).length, icon: <FaFilePdf />, tone: "neutral", isLoading: loading },
                ]}
            />

            <LibraryGroupModal
                isOpen={groupModalOpen}
                onClose={() => { dialogClosingRef.current = true; setGroupModalOpen(false); setEditingGroup(null); updateRouteState({ dialog: null }); }}
                group={editingGroup}
                onSave={saveGroup}
                isLoading={useActionApiIsLoading}
            />

            <LibraryPdfModal
                isOpen={pdfModalOpen}
                onClose={() => { dialogClosingRef.current = true; setPdfModalOpen(false); setEditingPdf(null); setUploadGroupId(""); updateRouteState({ dialog: null }); }}
                document={editingPdf}
                groups={groups}
                initialGroupId={uploadGroupId}
                onSave={savePdf}
                isLoading={useActionApiIsLoading}
            />

            <LibraryPdfReaderModal
                group={activeReaderGroup}
                documents={activeReaderGroup?.documents || []}
                isOpen={!!activeReaderGroup}
                onClose={closeReader}
                onOpened={(pdf) => mutatePdfs({ id: pdf.id, patch: { lastOpenedAt: new Date().toISOString() } })}
                onUpload={(group) => openUpload(group?.id || "")}
                onEdit={(pdf) => openEditPdf(pdf)}
                onToggleFavorite={toggleFavorite}
                onArchive={archivePdf}
                isLoading={loading}
            />
        </ViewLayout>
    );
};
